import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

export interface AdminUser {
  id: string;
  user_id: string;
  admin_level: 'super_admin' | 'template_admin' | 'support_admin' | 'content_admin';
  permissions: Record<string, boolean>;
  can_access_production: boolean;
  can_delete_content: boolean;
  can_manage_users: boolean;
  can_edit_templates: boolean;
  can_edit_ai_prompts: boolean;
  can_view_analytics: boolean;
  is_active: boolean;
}

export function useCurrentAdmin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [admin, setAdmin] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    loadAdminData();
  }, [user]);

  async function loadAdminData() {
    try {
      const { data, error } = await supabase
        .from('admin_users')
        .select('*')
        .eq('user_id', user?.id)
        .eq('is_active', true)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // Not an admin - redirect
          setError('Unauthorized access');
          toast({
            title: "Access Denied",
            description: "You don't have admin privileges",
            variant: "destructive",
          });
          navigate('/dashboard');
          return;
        }
        throw error;
      }

      setAdmin({
        ...data,
        permissions: (data.permissions as any) || {},
      } as AdminUser);
      
      // Log admin access
      await supabase.from('admin_audit_log').insert({
        admin_user_id: data.id,
        action_type: 'login',
        resource_type: 'admin_portal',
        action_details: { timestamp: new Date().toISOString() },
      });

      // Update last login
      await supabase
        .from('admin_users')
        .update({ last_login_at: new Date().toISOString() })
        .eq('id', data.id);

    } catch (err: any) {
      console.error('Error loading admin data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const hasPermission = (permission: keyof AdminUser): boolean => {
    if (!admin) return false;
    if (admin.admin_level === 'super_admin') return true;
    return admin[permission] === true;
  };

  const isSuperAdmin = (): boolean => {
    return admin?.admin_level === 'super_admin';
  };

  const isContentAdmin = (): boolean => {
    return admin?.admin_level === 'content_admin' || isSuperAdmin();
  };

  const isSupportAdmin = (): boolean => {
    return admin?.admin_level === 'support_admin' || isSuperAdmin();
  };

  return {
    admin,
    loading,
    error,
    hasPermission,
    isSuperAdmin,
    isContentAdmin,
    isSupportAdmin,
  };
}
